import React from 'react';
import { Check, TrendingUp, Calendar, Eye } from 'lucide-react';
import { Ranking, RankingVisualMode } from 'types/ranking';
import './RankingSelector.scss';

interface RankingSelectorProps {
  rankings: Ranking[];
  selectedRankingIds: string[];
  onSelectionChange: (rankingIds: string[]) => void;
  maxSelection?: number;
}

const RankingSelector: React.FC<RankingSelectorProps> = ({
  rankings,
  selectedRankingIds,
  onSelectionChange,
  maxSelection = 3
}) => {
  const handleToggleRanking = (rankingId: string) => {
    const isSelected = selectedRankingIds.includes(rankingId);
    
    if (isSelected) {
      // Remover seleção
      onSelectionChange(selectedRankingIds.filter(id => id !== rankingId));
    } else {
      // Adicionar seleção (respeitando limite)
      if (selectedRankingIds.length < maxSelection) {
        onSelectionChange([...selectedRankingIds, rankingId]);
      }
    }
  };

  const getMetricLabel = (metric: string): string => {
    const metricLabels: Record<string, string> = {
      points: 'Pontos',
      tasks: 'Tarefas',
      custom: 'Personalizada'
    };
    return metricLabels[metric] || metric;
  };

  const getPeriodLabel = (period: string): string => {
    const periodLabels: Record<string, string> = {
      semanal: 'Semanal',
      mensal: 'Mensal',
      anual: 'Anual'
    };
    return periodLabels[period] || period;
  };

  const getVisualLabel = (visual: RankingVisualMode): string => {
    const visualLabels: Record<RankingVisualMode, string> = {
      podio: 'Pódio',
      lista: 'Lista',
      privado: 'Privado',
      grid: 'Grid',
      medieval: '🏰 Medieval',
      tesouro: '💎 Tesouro',
      maquiagem: '💄 Maquiagem',
      tumba: '🏺 Tumba'
    };
    return visualLabels[visual];
  };

  if (rankings.length === 0) {
    return (
      <div className="ranking-selector-empty">
        <div className="empty-state">
          <TrendingUp size={48} />
          <h3>Nenhum ranking configurado</h3>
          <p>Crie um ranking na área de gestão para exibi-lo aqui.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ranking-selector">
      <div className="selector-header">
        <h2 className="selector-title">Selecione os Rankings para Exibir</h2>
        <p className="selector-subtitle">
          Escolha até {maxSelection} ranking(s) para visualização simultânea
        </p>
        <div className="selector-counter">
          {selectedRankingIds.length} / {maxSelection} selecionado(s)
        </div>
      </div>

      <div className="ranking-cards-grid">
        {rankings.map((ranking) => {
          if (!ranking._id) return null;
          
          const rankingId = ranking._id;
          const isSelected = selectedRankingIds.includes(rankingId);
          const isDisabled = !isSelected && selectedRankingIds.length >= maxSelection;

          return (
            <div
              key={rankingId}
              className={`ranking-card ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => !isDisabled && handleToggleRanking(rankingId)}
            >
              <div className="card-checkbox">
                {isSelected && (
                  <div className="checkbox-checked">
                    <Check size={16} />
                  </div>
                )}
                {!isSelected && <div className="checkbox-unchecked" />}
              </div>

              <div className="card-content">
                <h3 className="card-title">{ranking.title}</h3>

                <div className="card-details">
                  <div className="detail-item">
                    <TrendingUp size={16} />
                    <span>{getMetricLabel(ranking.metric)}</span>
                  </div>

                  <div className="detail-item">
                    <Calendar size={16} />
                    <span>{getPeriodLabel(ranking.period)}</span>
                  </div>

                  <div className="detail-item">
                    <Eye size={16} />
                    <span>{ranking.visuals.map(v => getVisualLabel(v)).join(', ')}</span>
                  </div>
                </div>

                <div className="card-footer">
                  <span className="participants-count">
                    {ranking.participants.length} participante(s)
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RankingSelector;
